/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.patients

import androidx.navigation.NavController
import androidx.navigation.NavGraphBuilder
import androidx.navigation.NavOptions
import androidx.navigation.navigation
import androidx.navigation.toRoute
import kotlinx.serialization.Serializable
import studio.oddpods.cauce.core.base.ui.composableWithPushTransitions
import studio.oddpods.cauce.core.base.ui.composableWithStayTransitions

@Serializable
data object PatientsDestination

@Serializable
data object PatientsListRoute

@Serializable
data object AddPatientWithSessionRoute

@Serializable
data class PatientDetailRoute(val patientId: String)

@Serializable
data class PatientEditRoute(val patientId: String)

fun NavController.navigateToPatients(navOptions: NavOptions? = null) {
    navigate(PatientsDestination, navOptions)
}

fun NavController.navigateToAddPatient(navOptions: NavOptions? = null) {
    navigate(AddPatientWithSessionRoute, navOptions)
}

fun NavController.navigateToPatientDetail(patientId: String, navOptions: NavOptions? = null) {
    navigate(PatientDetailRoute(patientId), navOptions)
}

fun NavController.navigateToPatientEdit(patientId: String, navOptions: NavOptions? = null) {
    navigate(PatientEditRoute(patientId), navOptions)
}

fun NavGraphBuilder.patientsGraph(
    navController: NavController,
    onPatientClick: (String) -> Unit,
    onLogSession: (String) -> Unit,
    onSendReminder: (String) -> Unit = {},
    onCreateRecurringSchedule: (String) -> Unit = {},
    onEditRecurringSchedule: (String, String) -> Unit = { _, _ -> },
    onSessionClick: (String) -> Unit = {},
) {
    navigation<PatientsDestination>(
        startDestination = PatientsListRoute,
    ) {
        composableWithStayTransitions<PatientsListRoute> {
            PatientListScreen(
                onPatientClick = onPatientClick,
                onAddPatientClick = { navController.navigateToAddPatient() },
            )
        }

        composableWithPushTransitions<AddPatientWithSessionRoute> {
            AddPatientWithSessionScreen(
                mode = AddPatientMode.Regular,
                onBackClick = navController::popBackStack,
            )
        }

        composableWithPushTransitions<PatientDetailRoute> {
            val route = it.toRoute<PatientDetailRoute>()
            PatientDetailScreen(
                patientId = route.patientId,
                onBackClick = navController::popBackStack,
                onLogSession = onLogSession,
                onSendReminder = onSendReminder,
                onEditPatient = { patientId -> navController.navigateToPatientEdit(patientId) },
                onCreateRecurringSchedule = onCreateRecurringSchedule,
                onEditRecurringSchedule = onEditRecurringSchedule,
                onSessionClick = onSessionClick,
            )
        }

        composableWithPushTransitions<PatientEditRoute> {
            val route = it.toRoute<PatientEditRoute>()
            PatientEditScreen(
                patientId = route.patientId,
                onBackClick = navController::popBackStack,
            )
        }
    }
}
